import React, { useState } from "react";
import { Button } from "@shared/ui/button";
import {
  FileText,
  Plus,
  ChevronDown,
  ChevronUp,
  Trash,
  Pencil,
  FolderPlus,
  InfoIcon,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@shared/ui/collapsible";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@shared/ui/accordion";

import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import {
  useConfigMapData,
  usePostConfigMap,
  usePatchConfigMap,
} from "@features/onboarding/utility-setup/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { ConfigMapKeys } from "@features/onboarding/configMap.constants";
import { logEvent } from "@shared/analytics/analytics";

export interface DocumentType {
  name: string;
  isActive: boolean;
  createdBy: string;
  description: string;
  createdDate: string;
  code: string;
  childCount: number;
  hierarchyNames: {
    documentType: string;
  };
  hierarchyCodes: {
    documentType: string;
  };
}

export interface DocumentSubType {
  name: string;
  isActive: boolean;
  createdBy: string;
  description: string;
  createdDate: string;
  code: string;
  childCount: number;
  hierarchyNames: {
    documentType: string;
    documentSubtype: string;
  };
  hierarchyCodes: {
    documentType: string;
    documentSubtype: string;
  };
}

interface DocumentTypeItemProps {
  item: DocumentType;
  onEdit: () => void;
  onDelete: () => void;
  subTypes: DocumentSubType[];
  onAddSubType: (parentCode: string) => void;
  onEditSubType: (subType: DocumentSubType) => void;
  onDeleteSubType: (subType: DocumentSubType) => void;
  isLoadingSubTypes: boolean;
  onToggleExpand: (typeCode: string, isOpen: boolean) => void;
  isExpanded: boolean;
}

const DocumentTypeItem: React.FC<DocumentTypeItemProps> = ({
  item,
  onEdit,
  onDelete,
  subTypes = [],
  onAddSubType,
  onEditSubType,
  onDeleteSubType,
  isLoadingSubTypes,
  onToggleExpand,
  isExpanded,
}) => {
  const handleToggle = () => {
    onToggleExpand(item.code, !isExpanded);
  };

  return (
    <div className="rounded-md border mb-3 transition-colors overflow-hidden">
      <div className="flex items-center justify-between p-3 hover:bg-muted/30">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-medium">{item.name}</h3>
            {item.childCount > 0 && (
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                {item.childCount} sub-types
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{item.description}</p>
          <p className="text-xs text-muted-foreground">
            Created by {item.createdBy} on{" "}
            {new Date(item.createdDate).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" onClick={handleToggle}>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={onEdit}>
            Edit
          </Button>
        </div>
      </div>

      <Collapsible open={isExpanded} onOpenChange={handleToggle}>
        <CollapsibleContent>
          <div className="bg-muted/20 p-3 border-t">
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-sm font-medium">Document Sub-Types</h4>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => onAddSubType(item.code)}
              >
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>

            {isLoadingSubTypes ? (
              <div className="text-xs text-muted-foreground">
                Loading sub-types...
              </div>
            ) : subTypes.length > 0 ? (
              <div className="space-y-1">
                {subTypes.map((sub) => (
                  <div
                    key={sub.code}
                    className="flex justify-between items-center p-2 bg-background rounded text-sm"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span>{sub.name}</span>
                      </div>
                      {sub.description && (
                        <p className="text-xs text-muted-foreground">
                          {sub.description}
                        </p>
                      )}
                    </div>
                    <div className="flex space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => onEditSubType(sub)}
                      >
                        <Pencil className="h-3 w-3 text-muted-foreground hover:text-primary" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                No sub-types defined
              </p>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

const DocumentTypeComponent: React.FC = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const [isAddingDocumentType, setIsAddingDocumentType] = useState(false);
  const [editingDocumentType, setEditingDocumentType] =
    useState<DocumentType | null>(null);
  const [isAddingSubType, setIsAddingSubType] = useState(false);
  const [isEditingSubType, setIsEditingSubType] = useState(false);
  const [editingSubType, setEditingSubType] = useState<DocumentSubType | null>(
    null
  );
  const [subTypeParentCode, setSubTypeParentCode] = useState<string | null>(
    null
  );

  // SIMPLIFIED: Only track which single document type is expanded
  const [expandedDocumentTypeCode, setExpandedDocumentTypeCode] = useState<
    string | null
  >(null);

  // Fetch document types
  const {
    data: documentTypes,
    isLoading: isLoadingDocumentTypes,
    refetch: refetchDocumentTypes,
  } = useConfigMapData<any>(remoteUtilityId, "DOCUMENT_JSON", "document_type");

  // Get the hierarchy code for the expanded document type
  const activeSubTypeCode = expandedDocumentTypeCode
    ? documentTypes?.result?.find(
        (type) => type.code === expandedDocumentTypeCode
      )?.hierarchyCodes?.documentType || null
    : null;

  // Fetch document subtypes for the expanded document type only
  const {
    data: documentSubTypes = [],
    isLoading: isLoadingSubTypes,
    refetch: refetchSubTypes,
  } = useConfigMapData<DocumentSubType[]>(
    remoteUtilityId,
    "DOCUMENT_JSON",
    "document_subtype",
    activeSubTypeCode || undefined // Only fetch when we have an expanded document type
  );

  // Mutation hooks
  const createUpdateMutation = usePostConfigMap();
  const patchMutation = usePatchConfigMap();

  // Form fields for document type
  const documentTypeFormFields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "Name",
      required: true,
      placeholder: "e.g. Invoice",
      fullWidth: true,
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "description",
      type: "text",
      label: "Description",
      required: false,
      placeholder: "e.g. Customer billing documents",
      fullWidth: true,
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors",
        error: "text-red-600 text-sm mt-1",
      },
    },
  ];

  // Form fields for sub-type
  const subTypeFormFields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "Name",
      required: true,
      placeholder: "e.g. Final Bill",
      fullWidth: true,
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors",
        error: "text-red-600 text-sm mt-1",
      },
    },
  ];

  // Create form instances
  const documentTypeForm = FormService.createForm({
    fields: documentTypeFormFields,
    mode: "onChange",
    validateOnMount: false,
  });

  const subTypeForm = FormService.createForm({
    fields: subTypeFormFields,
    mode: "onChange",
    validateOnMount: false,
  });

  const handleDocumentTypeSubmit = async (data: {
    name: string;
    description: string;
  }) => {
    try {
      if (editingDocumentType) {
        await patchMutation.mutateAsync(
          {
            remote_utility_id: remoteUtilityId,
            mapType: "DOCUMENT_JSON",
            code: editingDocumentType.hierarchyCodes.documentType,
            name: data.name,
            description: data.description,
          },
          {
            onSuccess: () => {
              logEvent("System Settings Document SubType Added", {
                remote_utility_id: remoteUtilityId,
                code: editingDocumentType.hierarchyCodes.documentType,
                name: data.name,
              });
            },
          }
        );
      } else {
        await createUpdateMutation.mutateAsync(
          {
            remote_utility_id: remoteUtilityId,
            mapType: "DOCUMENT_JSON",
            code: data.name,
            name: data.name,
            description: data.description,
          },
          {
            onSuccess: () => {
              logEvent("System Settings Document Type Added", {
                remote_utility_id: remoteUtilityId,
                code: data.name,
                name: data.name,
              });
            },
          }
        );
      }

      documentTypeForm.reset();
      setIsAddingDocumentType(false);
      setEditingDocumentType(null);
      refetchDocumentTypes();
    } catch (error) {
      console.error("Document type operation failed:", error);
    }
  };

  const handleSubTypeSubmit = async (data: { name: string }) => {
    try {
      if (isEditingSubType && editingSubType) {
        await patchMutation.mutateAsync({
          remote_utility_id: remoteUtilityId,
          mapType: "DOCUMENT_JSON",
          code: editingSubType.hierarchyCodes.documentSubtype,
          name: data.name,
        });
        setIsEditingSubType(false);
        setEditingSubType(null);
      } else if (subTypeParentCode) {
        const parentDocumentType = documentTypes.result.find(
          (type) => type.code === subTypeParentCode
        );
        if (parentDocumentType) {
          const finalCode = `${parentDocumentType.hierarchyCodes.documentType}_${data.name}`;

          await createUpdateMutation.mutateAsync({
            remote_utility_id: remoteUtilityId,
            mapType: "DOCUMENT_JSON",
            name: data.name,
            code: finalCode,
          });
        }
      }

      subTypeForm.reset();
      setIsAddingSubType(false);
      setSubTypeParentCode(null);
      refetchSubTypes();
    } catch (error) {
      console.error("Document sub-type operation failed:", error);
    }
  };

  // Event handlers
  const startAddDocumentType = () => {
    documentTypeForm.reset();
    setEditingDocumentType(null);
    setIsAddingDocumentType(true);
  };

  const editDocumentType = (documentType: DocumentType) => {
    setEditingDocumentType(documentType);
    documentTypeForm.setValue("name", documentType.name);
    documentTypeForm.setValue("description", documentType.description);
    setIsAddingDocumentType(true);
  };

  const deleteDocumentType = async (documentType: DocumentType) => {
    try {
      // TODO: Implement API call to delete document type
      refetchDocumentTypes();
    } catch (error) {
      console.error("Failed to delete document type:", error);
    }
  };

  const startAddSubType = (parentCode: string) => {
    setSubTypeParentCode(parentCode);
    setIsEditingSubType(false);
    setEditingSubType(null);
    setIsAddingSubType(true);
    subTypeForm.reset();
  };

  const editSubType = (subType: DocumentSubType) => {
    setIsEditingSubType(true);
    setEditingSubType(subType);

    // Find the parent document type code from the sub-type's hierarchy
    const parentDocumentTypeCode = documentTypes.result.find(
      (type) =>
        type.hierarchyCodes.documentType === subType.hierarchyCodes.documentType
    )?.code;

    setSubTypeParentCode(parentDocumentTypeCode || null);
    subTypeForm.setValue("name", subType.name);
    setIsAddingSubType(true);
  };

  const deleteSubType = async (subType: DocumentSubType) => {
    try {
      // TODO: Implement API call to delete sub-type
      refetchSubTypes();
    } catch (error) {
      console.error("Failed to delete sub-type:", error);
    }
  };

  const cancelDocumentTypeEdit = () => {
    setIsAddingDocumentType(false);
    setEditingDocumentType(null);
    documentTypeForm.reset();
  };

  const cancelSubTypeAdd = () => {
    setIsAddingSubType(false);
    setIsEditingSubType(false);
    setEditingSubType(null);
    setSubTypeParentCode(null);
    subTypeForm.reset();
  };

  // SIMPLIFIED: Handle document type expansion (only one at a time)
  const handleDocumentTypeToggle = (typeCode: string, isOpen: boolean) => {
    if (isOpen) {
      // Open this document type (and close any other)
      setExpandedDocumentTypeCode(typeCode);
    } else {
      // Close this document type
      setExpandedDocumentTypeCode(null);
    }
  };

  // Get sub-types for a specific document type
  const getSubTypesForDocumentType = (typeCode: string): DocumentSubType[] => {
    // Only return sub-types if this is the currently expanded document type
    if (expandedDocumentTypeCode === typeCode) {
      if (Array.isArray(documentSubTypes)) {
        return documentSubTypes;
      }
      // Handle paginated response
      if (documentSubTypes && Array.isArray((documentSubTypes as any).result)) {
        return (documentSubTypes as any).result;
      }
    }

    return [];
  };

  // Check if loading sub-types for a specific document type
  const isLoadingSubTypesForDocumentType = (typeCode: string): boolean => {
    return expandedDocumentTypeCode === typeCode && isLoadingSubTypes;
  };

  if (isLoadingDocumentTypes) {
    return (
      <div className="mb-8">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-md bg-primary/10">
              <FileText className="h-4 w-4 text-primary" />
            </div>
            <h3 className="font-medium">Document Types</h3>
          </div>
        </div>
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground">
            Loading document types...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8 border rounded-md p-4">
      <div className="flex justify-between items-center mb-3">
        <div className="flex flex-col ">
         <h3 className="card-heading-text">Document Types</h3>
          <p className="card-subheading-text">
            Define and manage document types with their respective sub-types.
          </p>
        </div>
        {!isAddingDocumentType && !isAddingSubType && (
          <Button size="sm" onClick={startAddDocumentType}>
            <Plus className="h-4 w-4 mr-1" /> Add Document Type
          </Button>
        )}
      </div>
      <Accordion type="single" collapsible className="mb-4">
        <AccordionItem value="help">
          <AccordionTrigger className="text-sm font-medium">
            <div className="flex items-center">
              <InfoIcon className="h-4 w-4 mr-2" />
              Need help with Documents?
            </div>
          </AccordionTrigger>
          <AccordionContent className="text-sm text-muted-foreground">
            <p className="mb-2">
              Define and manage document types and sub-types in SMART360:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Create standardized document categories (e.g., ID Proof,
                Agreements, Permits).
              </li>
              <li>
                Add sub-types for detailed classification (e.g., Passport,
                Driver’s License under ID Proof).
              </li>
              <li>
                Use types and sub-types for consistent document management.
              </li>
              <li>
                Ensure correct tagging for quick retrieval and compliance
                tracking.
              </li>
            </ul>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {isAddingDocumentType ? (
        <div className="border rounded-md p-4 mb-4">
          <h3 className="font-medium mb-4">
            {editingDocumentType
              ? "Edit Document Type"
              : "Add New Document Type"}
          </h3>
          <DynamicForm
            fields={documentTypeFormFields}
            form={documentTypeForm}
            onSubmit={handleDocumentTypeSubmit}
            onReset={cancelDocumentTypeEdit}
            loading={createUpdateMutation.isPending || patchMutation.isPending}
            config={{
              showProgress: false,
              layout: "vertical",
              submitButtonText: editingDocumentType ? "Update" : "Add",
              showResetButton: true,
              resetButtonText: "Cancel",
              resetOnSubmit: false,
              classes: {
                form: "space-y-4",
                fieldContainer: "",
                buttonContainer: "flex justify-end space-x-2 pt-4",
                fieldGroup: "",
              },
            }}
            style={{
              background: "transparent",
              border: "none",
              boxShadow: "none",
            }}
          />
        </div>
      ) : isAddingSubType ? (
        <div className="border rounded-md p-4 mb-4">
          <h3 className="font-medium mb-4">
            {isEditingSubType
              ? "Edit Document Sub-Type"
              : "Add New Document Sub-Type"}
          </h3>
          <DynamicForm
            fields={subTypeFormFields}
            form={subTypeForm}
            onSubmit={handleSubTypeSubmit}
            onReset={cancelSubTypeAdd}
            loading={createUpdateMutation.isPending || patchMutation.isPending}
            config={{
              showProgress: false,
              layout: "vertical",
              submitButtonText: isEditingSubType ? "Update" : "Add",
              showResetButton: true,
              resetButtonText: "Cancel",
              resetOnSubmit: false,
              classes: {
                form: "space-y-4",
                fieldContainer: "",
                buttonContainer: "flex justify-end space-x-2 pt-4",
                fieldGroup: "",
              },
            }}
            style={{
              background: "transparent",
              border: "none",
              boxShadow: "none",
            }}
          />
        </div>
      ) : documentTypes.result.length > 0 ? (
        <div>
          {documentTypes.result.map((documentType) => (
            <DocumentTypeItem
              key={documentType.code}
              item={documentType}
              onEdit={() => editDocumentType(documentType)}
              onDelete={() => deleteDocumentType(documentType)}
              subTypes={getSubTypesForDocumentType(documentType.code)}
              onAddSubType={startAddSubType}
              onEditSubType={editSubType}
              onDeleteSubType={deleteSubType}
              isLoadingSubTypes={isLoadingSubTypesForDocumentType(
                documentType.code
              )}
              onToggleExpand={handleDocumentTypeToggle}
              isExpanded={expandedDocumentTypeCode === documentType.code}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-6 border rounded-md bg-muted/30">
          <FolderPlus className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
          <h3 className="font-medium mb-1">No Document Types</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
            Create document types to organize your files, invoices, and
            agreements.
          </p>
          <Button onClick={startAddDocumentType}>
            <Plus className="h-4 w-4 mr-1" /> Add Your First Document Type
          </Button>
        </div>
      )}
    </div>
  );
};

export default DocumentTypeComponent;
